import { useCallback, useEffect, useRef, useState } from 'react';
import { useBaoSelector } from 'utils/hooks/redux';
import {
  fetchCompanyDetails,
  fetchPaymentInvoiceUrl,
} from 'molecules/PaymentOptionRadio/helpers';
import { isIndiaRegion } from 'utils/helpers/qbRegion';

export type RedirectResult = { ok: true } | { ok: false; message: string };

const DEFAULT_REDIRECT_ERROR =
  'Could not open the invoice payment page. Please try again.';

export const useIndiaInvoiceRedirect = () => {
  const { currentCompany } = useBaoSelector((state) => state.common);

  const [qbRegion, setQbRegion] = useState<string | undefined>(undefined);
  const [redirectingId, setRedirectingId] = useState<string | null>(null);
  const regionPromiseRef = useRef<Promise<string | undefined> | undefined>(
    undefined
  );

  useEffect(() => {
    if (!currentCompany) {
      regionPromiseRef.current = undefined;
      setQbRegion(undefined);
      return;
    }

    const promise = fetchCompanyDetails(currentCompany.company_hs_id)
      .then((company) => company?.qb_region as string | undefined)
      .catch(() => undefined);

    regionPromiseRef.current = promise;
    promise.then(setQbRegion);
  }, [currentCompany]);

  const ensureIsIndiaRegion = useCallback(async (): Promise<boolean> => {
    if (qbRegion !== undefined) return isIndiaRegion(qbRegion);
    const region = await regionPromiseRef.current;
    return isIndiaRegion(region);
  }, [qbRegion]);

  const redirectToInvoiceUrl = useCallback(
    async (invoiceId: string): Promise<RedirectResult> => {
      try {
        setRedirectingId(invoiceId);
        const url = await fetchPaymentInvoiceUrl(invoiceId);
        window.open(url, '_blank');
        return { ok: true };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        return {
          ok: false,
          message:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            DEFAULT_REDIRECT_ERROR,
        };
      } finally {
        setRedirectingId(null);
      }
    },
    []
  );

  return {
    isIndiaRegion: isIndiaRegion(qbRegion),
    ensureIsIndiaRegion,
    redirectToInvoiceUrl,
    redirectingId,
  };
};
